import { Injectable } from '@angular/core';
import { ColumnState, ICombinedSimpleModel, IRowNode, TextFilterModel } from 'ag-grid-community';
import { FilterModel } from '../../../models/locations-models/filter-map';
import { LSCField } from '../../../models/LSCFields';
import { cloneDeep } from 'lodash';
import { Location } from '../../../models/locations-models/location';
import { AgApiService } from '../../global/gx-ag-grid/ag-api.service';

@Injectable()
export class LocationListApiService extends AgApiService<Location>{

  /**
   * Refocus a previously focused cell before the next repaint = no flicker
   */
  refocusCell() {
    const focusedCell = this.gridApi.getFocusedCell();
    requestAnimationFrame(() => {
      this._gridApi.setFocusedCell(focusedCell.rowIndex, focusedCell.column.getId());
    });
  }

  /**
   * Clears all filters in the data grid which will trigger the onFilterChanged event to which we're
   * listening to in the component and that will mirror the change in local store service
   */
  clearFilters() {
    this._gridApi?.setFilterModel(null);
  }

  /**
   * Sets filters in the data grid which will trigger the onFilterChanged event to which we're
   * listening to in the component and that will mirror the change in local store service
   * Setting individual filterModel key values to 'null' will effectively reset the respective filters
   * @param filterModel
   */
  setFilters(filterModel: FilterModel) {
    this._gridApi?.setFilterModel(filterModel);
    this._gridApi?.onFilterChanged();
  }

  /**
   * Sets single filter in the data grid which will trigger the onFilterChanged event to which we're
   * listening to in the component and that will mirror the change in local store service
   * Providing filterModel value of 'null' will effectively reset the filter
   * @param filterModel
   * @param colId
   */
  setFilter(colId: LSCField, filterModel: TextFilterModel | ICombinedSimpleModel<TextFilterModel> | string[] | {from: string, to: string}) {
    this._gridApi?.setColumnFilterModel(colId, filterModel).then(() => this._gridApi?.onFilterChanged());
  }

  /**
   * Set column state
   * @param columnState
   * @param applyOrder Whether column order should be applied.
   */
  setColumnState(columnState: ColumnState[], applyOrder = true) {
    this._gridApi?.applyColumnState({ state: columnState, applyOrder });
  }

  /**
   * Toggle single column's visibility.
   * @param colId
   * @param show
   */
  toggleColumnVisibility(colId: LSCField, show: boolean) {
    const columnState = this.gridApi?.getColumnState();
    const idColumnIndex = columnState.findIndex(cs => cs.colId === colId);

    if (idColumnIndex >= 0) {
      const nextState = cloneDeep(columnState);
      nextState[idColumnIndex].hide = !show;
      this.setColumnState(nextState);
    }
  }

  /**
   * reset sorting for all columns
   */
  clearSorting() {
    const newColumnState = this._gridApi?.getColumnState();
    newColumnState.forEach(col => {
      col.sort = null;
      col.sortIndex = null;
    });
    this.setColumnState(newColumnState);
  }

  setPageSize(pageSize: number) {
    this._gridApi.setGridOption('paginationPageSize', pageSize)
  }

  /**
   * @param pageIndex 0-based
   */
  goToPage(pageIndex: number) {
    this._gridApi.paginationGoToPage(pageIndex);
  }

  getRowById(locationId: string): IRowNode<Location> {
    return this._gridApi?.getRowNode(locationId);
  }

  getRowByIndex(index: number): IRowNode<Location> {
    if (isNaN(index) || index < 0) {
      return undefined;
    }

    return this._gridApi?.getDisplayedRowAtIndex(index);
  }

  getFilteredLocations() {
    const filteredLocations: Location[] = [];
    this._gridApi?.forEachNodeAfterFilterAndSort(rowNode =>
        filteredLocations.push(rowNode.data));
    return filteredLocations;
  }


}
